import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, CheckCircle2, ChevronRight, Loader2, LifeBuoy } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

// cc_12 checklist routes are MOBILE expo-router paths — map them to web routes, never deep-link raw.
const ROUTE_MAP = {
  "/new-chat": "/app/chats",
  "/story/create": "/app/chats",
  "/bloqs": "/app/bloqs",
  "/calls": "/app/chats",
  "/assistant": "/app/assistant",
};

function GettingStarted() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["help-checklist"], queryFn: api.helpChecklist, staleTime: 30000 });
  if (isLoading) return null;
  const tasks = data?.tasks || [];
  if (!tasks.length) return null;
  const done = data?.done ?? tasks.filter((t) => t.done).length;
  const total = data?.total ?? tasks.length;
  const allDone = done >= total;

  return (
    <div className="mb-6 rounded-3xl border border-bloq-mint/25 bg-bloq-mint/[0.06] p-4" data-testid="getting-started-card">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-bold text-white">Getting started</div>
        <span className="rounded-full bg-bloq-mint/15 px-2 py-0.5 text-[11px] font-bold text-bloq-mint" data-testid="getting-started-progress">{done}/{total}</span>
      </div>
      {allDone && <div className="mb-3 text-xs text-bloq-mint">🎉 You've explored the essentials — nice work.</div>}
      <div className="space-y-1.5">
        {tasks.map((t) => {
          const to = ROUTE_MAP[t.route] || "/app/chats";
          return (
            <button
              key={t.id}
              onClick={() => !t.done && navigate(to)}
              disabled={t.done}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition-colors",
                t.done ? "cursor-default border-bloq-mint/20 bg-bloq-mint/[0.06]" : "border-white/8 bg-bloq-s2 hover:bg-white/5",
              )}
              data-testid={`getting-started-task-${t.id}`}
            >
              {t.done ? <CheckCircle2 className="h-5 w-5 shrink-0 text-bloq-mint" /> : <span className="w-5 shrink-0 text-center text-lg">{t.emoji}</span>}
              <div className="min-w-0 flex-1">
                <div className={cn("text-sm font-semibold", t.done ? "text-bloq-mint line-through" : "text-white")}>{t.title}</div>
                {!t.done && t.hint && <div className="truncate text-[11px] text-bloq-muted">{t.hint}</div>}
              </div>
              {!t.done && <ChevronRight className="h-4 w-4 shrink-0 text-bloq-muted" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function HelpCenterPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["help-articles"], queryFn: api.helpArticles, staleTime: 300000 });
  const sections = data?.sections || [];

  return (
    <div className="mx-auto max-w-2xl px-4 py-6" data-testid="help-center-page">
      <div className="mb-5 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/70 transition-colors hover:text-white" data-testid="help-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <LifeBuoy className="h-5 w-5 text-bloq-mint" />
          <h1 className="text-2xl font-bold">Help Center</h1>
        </div>
      </div>

      <GettingStarted />

      {isLoading ? (
        <div className="flex justify-center py-10 text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          {sections.map((s) => (
            <div key={s.id} data-testid={`help-section-${s.id}`}>
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-white">
                <span className="text-lg">{s.emoji}</span> {s.title}
              </div>
              <Accordion type="single" collapsible className="rounded-2xl border border-white/8 bg-bloq-s1 px-3">
                {(s.articles || []).map((a, i) => (
                  <AccordionItem key={i} value={`${s.id}-${i}`} className="border-white/8" data-testid={`help-article-${s.id}-${i}`}>
                    <AccordionTrigger className="text-left text-sm font-semibold text-white hover:no-underline">{a.q}</AccordionTrigger>
                    <AccordionContent className="whitespace-pre-line text-sm text-bloq-muted">{a.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
